import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  EMBED_PAUSE_SCREEN_VIEWED,
  SHARE_PAGE_PAUSE_BUTTON_CLICKED,
} from 'constants/events';
import {
  selectHasEnded,
  selectIsMainVideoOnSharePage,
  selectPlaying,
  selectSeeking,
  selectShowingComments,
  selectTheaterMode,
  suppressPlayAnimation,
} from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { isMobile } from 'utilities/device';
import { inEmbedPlayer } from 'utilities/url';

import SvgPause from 'img/video-player/pause_overlay.svg';
import SvgPlay from 'img/video-player/play_overlay.svg';

import { getFlagValueForUser } from '../../../utilities/featureFlag';
import {
  EXP_EMBED_PAUSE_VIDEO_NUDGES,
  EXP_SHARE_PAGE_PAUSE_VIDEO_NUDGES,
} from '../../../../../shared/constants/featureFlag';

import styles from './styles.module.less';

const PlayPauseOverlay = () => {
  const playing = useSelector(selectPlaying);

  const inTheaterMode = useSelector(selectTheaterMode);

  const hasEnded = useSelector(selectHasEnded);
  const showingAllComments = useSelector(selectShowingComments);
  const suppressAnimations = useSelector(suppressPlayAnimation);
  const seeking = useSelector(selectSeeking);
  const isMainVideoOnSharePage = useSelector(selectIsMainVideoOnSharePage);

  const [animatePlay, setAnimatePlay] = useState(false);
  const [animatePause, setAnimatePause] = useState(false);

  const [prevPlaying, setPrevPlaying] = useState(playing);
  const [pauseNudgeValue, setPauseNudgeValue] = useState(null);

  const showPauseNudge =
    pauseNudgeValue === 'variant-1' || pauseNudgeValue === 'variant-2';

  useEffect(() => {
    if (playing != prevPlaying) {
      const showPlayAnimation = playing && !suppressAnimations && !seeking;
      const showPauseAnimation =
        !playing &&
        !hasEnded &&
        !showingAllComments &&
        !seeking &&
        !suppressAnimations;

      if (showPauseAnimation && inEmbedPlayer()) {
        analytics.track(EMBED_PAUSE_SCREEN_VIEWED);
      } else if (showPauseAnimation && isMainVideoOnSharePage) {
        analytics.track(SHARE_PAGE_PAUSE_BUTTON_CLICKED);
      }

      if (isMainVideoOnSharePage && showPauseAnimation) {
        getFlagValueForUser(EXP_SHARE_PAGE_PAUSE_VIDEO_NUDGES);
      } else if (showPauseAnimation && inEmbedPlayer()) {
        getFlagValueForUser(EXP_EMBED_PAUSE_VIDEO_NUDGES);
      }

      setAnimatePlay(showPlayAnimation);
      setAnimatePause(showPauseAnimation);

      setPrevPlaying(playing);
    }
  }, [
    hasEnded,
    playing,
    prevPlaying,
    seeking,
    showingAllComments,
    suppressAnimations,
    isMainVideoOnSharePage,
  ]);

  useEffect(() => {
    const showPauseAnimation =
      !playing &&
      !hasEnded &&
      !showingAllComments &&
      !seeking &&
      !suppressAnimations;

    const getFlags = async () => {
      if (!showPauseAnimation) {
        return;
      }

      if (isMainVideoOnSharePage) {
        const val = await getFlagValueForUser(
          EXP_SHARE_PAGE_PAUSE_VIDEO_NUDGES,
          false,
          { isMobile }
        );

        setPauseNudgeValue(val);
      } else if (inEmbedPlayer()) {
        const val = await getFlagValueForUser(
          EXP_EMBED_PAUSE_VIDEO_NUDGES,
          false,
          { isMobile }
        );

        setPauseNudgeValue(val);
      }
    };

    getFlags();
  }, [
    hasEnded,
    playing,
    prevPlaying,
    seeking,
    showingAllComments,
    suppressAnimations,
    isMainVideoOnSharePage,
  ]);

  useEffect(() => {
    // cut off animation early if we enter/exit theater mode,
    // otherwise it would reset
    setAnimatePlay(false);
    setAnimatePause(false);
  }, [inTheaterMode]);

  return (
    <div className={styles.playPauseOverlay}>
      <img
        onAnimationEnd={() => {
          setAnimatePlay(false);
        }}
        className={cx(styles.playPauseIcon, { [styles.animate]: animatePlay })}
        src={SvgPlay}
      />
      {!showPauseNudge && (
        <img
          onAnimationEnd={() => {
            setAnimatePause(false);
          }}
          className={cx(styles.playPauseIcon, {
            [styles.animate]: animatePause,
          })}
          src={SvgPause}
        />
      )}
    </div>
  );
};

export default PlayPauseOverlay;
